import clone from 'clone-deep';
import { stripUndefined } from '@pushly/aquarium-utils/lib/common/helpers/object';

export interface IFaviconResource {
    rel: string,
    href: string,
}

export class WhitelabelConfigModel {
    public static build(props: WhitelabelConfigModel | any): WhitelabelConfigModel {
        const data = props instanceof WhitelabelConfigModel ? props.serialize() : clone(props);

        const model = new WhitelabelConfigModel();
        model.setId(data.id);
        model.setIsActive(data.is_active);
        model.setDisplayName(data.display_name);
        model.setHostname(data.hostname);
        model.setPreviewDomainId(data.preview_domain_id);
        model.setPreviewDomainKey(data.preview_domain_key);
        model.setDocumentationLink(data.documentation_link ?? null);
        model.setSupportEmailAddress(data.support_email_address ?? null);
        model.setFaviconResources(data.favicon_resources);
        model.setLogoUrl(data.logo_url ?? null);
        model.setPrimaryIconUrl(data.primary_icon_url ?? null);
        model.setSidebarIconUrl(data.sidebar_icon_url ?? null);
        model.setLoginBackgroundUrl(data.login_background_url ?? null);
        model.setPrimaryColorHex(data.primary_color_hex ?? null);
        model.setSecondaryColorHex(data.secondary_color_hex ?? null);
        model.setTertiaryColorHex(data.tertiary_color_hex ?? null);
        model.setCreatedAt(data.created_at);
        model.setCreatedBy(data.created_by);
        model.setCreatedByUserName(data.created_by_user_name);

        return model;
    }

    private id: number;
    private displayName: string;
    private hostname: string;
    private isActive: boolean;
    private previewDomainId: number;
    private previewDomainKey: string;
    private documentationLink: string | null;
    private supportEmailAddress: string | null;
    private loginBackgroundUrl: string | null;
    private faviconResources: IFaviconResource[];
    private logoUrl: string | null;
    private primaryIconUrl: string | null;
    private sidebarIconUrl: string | null;
    private primaryColorHex: string | null;
    private secondaryColorHex: string | null;
    private tertiaryColorHex: string | null;
    private createdAt: Date | string;
    private createdBy: string;
    private createdByUserName: string;

    public clone(): WhitelabelConfigModel {
        return WhitelabelConfigModel.build(this);
    }

    public serialize(_: boolean = true): any {
        return stripUndefined({
            id: this.getId(),
            display_name: this.getDisplayName(),
            is_active: this.getIsActive(),
            hostname: this.getHostname(),
            preview_domain_id: this.getPreviewDomainId(),
            preview_domain_key: this.getPreviewDomainKey(),
            documentation_link: this.getDocumentationLink(),
            support_email_address: this.getSupportEmailAddress(),
            login_background_url: this.getLoginBackgroundUrl(),
            favicon_resources: this.getFaviconResources(),
            logo_url: this.getLogoUrl(),
            primary_icon_url: this.getPrimaryIconUrl(),
            sidebar_icon_url: this.getSidebarIconUrl(),
            primary_color_hex: this.getPrimaryColorHex(),
            secondary_color_hex: this.getSecondaryColorHex(),
            tertiary_color_hex: this.getTertiaryColorHex(),
            created_at: this.getCreatedAt(),
            created_by: this.getCreatedBy(),
            created_by_user_name: this.getCreatedByUserName(),
        });
    }

    public getId(): number { return this.id; }
    public setId(id: number) { this.id = id; }

    public getDisplayName() { return this.displayName; }
    public setDisplayName(displayName: string) { this.displayName = displayName; }

    public getHostname() { return this.hostname; }
    public setHostname(hostname: string) { this.hostname = hostname; }

    public getIsActive() { return this.isActive; }
    public setIsActive(isActive: boolean) { this.isActive = isActive; }

    public getPreviewDomainId() { return this.previewDomainId; }
    public setPreviewDomainId(id: number) { this.previewDomainId = id; }

    public getPreviewDomainKey() { return this.previewDomainKey; }
    public setPreviewDomainKey(domainKey: string) { this.previewDomainKey = domainKey; }

    public getDocumentationLink() { return this.documentationLink; }
    public setDocumentationLink(link: string | null) { this.documentationLink = link; }

    public getSupportEmailAddress() { return this.supportEmailAddress; }
    public setSupportEmailAddress(addr: string | null) { this.supportEmailAddress = addr; }

    public getLoginBackgroundUrl() { return this.loginBackgroundUrl; }
    public setLoginBackgroundUrl(url: string | null) { this.loginBackgroundUrl = url; }

    public getFaviconResources() { return this.faviconResources; }
    public setFaviconResources(resources: any) { this.faviconResources = resources; }

    public getLogoUrl() { return this.logoUrl; }
    public setLogoUrl(url: string | null) { this.logoUrl = url; }

    public getPrimaryIconUrl() { return this.primaryIconUrl; }
    public setPrimaryIconUrl(url: string | null) { this.primaryIconUrl = url; }

    public getSidebarIconUrl() { return this.sidebarIconUrl; }
    public setSidebarIconUrl(url: string) { this.sidebarIconUrl = url; }

    public getPrimaryColorHex() { return this.primaryColorHex; }
    public setPrimaryColorHex(hex: string | null) { this.primaryColorHex = hex; }

    public getSecondaryColorHex() { return this.secondaryColorHex; }
    public setSecondaryColorHex(hex: string | null) { this.secondaryColorHex = hex; }

    public getTertiaryColorHex() { return this.tertiaryColorHex; }
    public setTertiaryColorHex(hex: string | null) { this.tertiaryColorHex = hex; }

    public getCreatedAt() { return this.createdAt; }
    public setCreatedAt(date: Date | string) { this.createdAt = date; }

    public getCreatedBy() { return this.createdBy; }
    public setCreatedBy(userId: string) { this.createdBy = userId; }

    public getCreatedByUserName() { return this.createdByUserName; }
    public setCreatedByUserName(userName: string) { this.createdByUserName = userName; }
}
